import { useState, useCallback, useEffect } from 'react';
import { X, Library, Loader2 } from 'lucide-react';
import { Button } from '../Button';
import { Badge } from '../Badge';
import { DocumentUploader } from '../Documents/DocumentUploader';
import { DocumentList } from '../Documents/DocumentList';
import type { Expense, Reimbursement, ExpenseDocument, LibraryDocument } from '@/api/client';
import { api } from '@/api/client';
import { AccessibleDialog } from '@/components/AccessibleDialog';

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: Expense | null;
  onReimburse?: (expense: Expense) => void;
  onDelete?: (expenseId: string) => void;
  onEditReimbursement?: (reimbursement: Reimbursement) => void;
  onCancelReimbursement?: (reimbursementId: string) => void;
  onExpenseUpdated?: (expense: Expense) => void;
}

const STATUS_VARIANT: Record<string, 'green' | 'gold' | 'gray'> = {
  FullyReimbursed: 'green',
  PartiallyReimbursed: 'gold',
  Unreimbursed: 'gray',
};

const STATUS_LABEL: Record<string, string> = {
  FullyReimbursed: 'Fully Reimbursed',
  PartiallyReimbursed: 'Partially Reimbursed',
  Unreimbursed: 'Unreimbursed',
};

export function ExpenseModal({
  isOpen,
  onClose,
  expense,
  onReimburse,
  onDelete,
  onEditReimbursement,
  onCancelReimbursement,
  onExpenseUpdated,
}: ExpenseModalProps) {
  const [localDocs, setLocalDocs] = useState<ExpenseDocument[]>([]);
  const [initialized, setInitialized] = useState<string | null>(null);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState<LibraryDocument[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [linkingDocId, setLinkingDocId] = useState<string | null>(null);

  // Sync local docs when expense changes
  if (expense && expense.id !== initialized) {
    setLocalDocs(expense.documents || []);
    setInitialized(expense.id);
    setShowLibraryPicker(false);
  }

  const fetchLibraryDocs = useCallback(async () => {
    setLoadingLibrary(true);
    try {
      const docs = await api.getLibraryDocuments({ linked: false });
      setLibraryDocs(docs || []);
    } catch (err) {
      console.error('Failed to fetch library documents:', err);
    } finally {
      setLoadingLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (showLibraryPicker) fetchLibraryDocs();
  }, [showLibraryPicker, fetchLibraryDocs]);

  const handleLinkFromLibrary = async (doc: LibraryDocument) => {
    if (!expense) return;
    setLinkingDocId(doc.id);
    try {
      await api.linkDocument(doc.id, expense.id);
      // Add to local docs list
      const linked: ExpenseDocument = {
        id: doc.id,
        fileName: doc.fileName,
        contentType: doc.contentType,
        fileSize: doc.fileSize,
        description: doc.description,
        documentType: doc.documentType,
        source: doc.source,
        createdAt: doc.createdAt,
      };
      setLocalDocs(prev => [linked, ...prev]);
      // Remove from library picker list
      setLibraryDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      console.error('Failed to link document:', err);
    } finally {
      setLinkingDocId(null);
    }
  };

  const handleUploadComplete = useCallback((doc: ExpenseDocument) => {
    setLocalDocs(prev => [doc, ...prev]);
  }, []);

  const handleDocumentDeleted = useCallback((documentId: string) => {
    setLocalDocs(prev => prev.filter(d => d.id !== documentId));
  }, []);

  if (!expense) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const activeReimbursements = expense.reimbursements?.filter(r => r.status === 'Active') || [];
  const cancelledReimbursements = expense.reimbursements?.filter(r => r.status === 'Cancelled') || [];

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onClose={onClose}
      labelledById="expense-modal-title"
      backdropClassName="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      className="bg-white rounded-lg w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-2xl"
    >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
          <h2 id="expense-modal-title" className="text-[20px] font-semibold text-[#1B365D]">Expense Details</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#718096] hover:text-[#4A5568]">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Draft Banner */}
          {expense.isDraft && (
            <div className="bg-[#FFF8E1] border border-[#C9A227] rounded-lg px-4 py-3 flex items-start gap-3">
              <span className="text-[#C9A227] text-[18px] leading-none mt-0.5">&#9888;</span>
              <div>
                <div className="text-[14px] font-medium text-[#1B365D]">Draft Expense</div>
                <div className="text-[13px] text-[#4A5568] mt-0.5">
                  This expense was created from a forwarded email. Review and complete the details.
                </div>
              </div>
            </div>
          )}

          {/* Provider & Description */}
          <div>
            <label className="block text-[12px] font-semibold text-[#718096] mb-1">Provider</label>
            <div className="text-[16px] font-medium text-[#1B365D]">{expense.provider}</div>
          </div>

          {expense.description && (
            <div>
              <label className="block text-[12px] font-semibold text-[#718096] mb-1">Description</label>
              <div className="text-[14px] text-[#4A5568]">{expense.description}</div>
            </div>
          )}

          {/* Amount & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#718096] mb-1">Amount</label>
              <div className="text-[24px] font-semibold text-[#1B365D] font-mono">
                {formatCurrency(expense.amount)}
              </div>
              {expense.amountReimbursed > 0 && expense.amountRemaining > 0 && (
                <div className="text-[12px] text-[#718096] mt-1">
                  {formatCurrency(expense.amountRemaining)} remaining
                </div>
              )}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#718096] mb-1">Status</label>
              <Badge variant={STATUS_VARIANT[expense.status] || 'gray'} size="default">
                {STATUS_LABEL[expense.status] || expense.status}
              </Badge>
            </div>
          </div>

          {/* Date & Family Member */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#718096] mb-1">Service Date</label>
              <div className="text-[14px] text-[#4A5568]">{formatDate(expense.serviceDate)}</div>
            </div>
            {expense.familyMemberName && (
              <div>
                <label className="block text-[12px] font-semibold text-[#718096] mb-1">Family Member</label>
                <div className="text-[14px] text-[#4A5568]">{expense.familyMemberName}</div>
              </div>
            )}
          </div>

          {/* Notes */}
          {expense.notes && (
            <div>
              <label className="block text-[12px] font-semibold text-[#718096] mb-1">Notes</label>
              <div className="text-[14px] text-[#4A5568] whitespace-pre-wrap">{expense.notes}</div>
            </div>
          )}

          {/* Documents */}
          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-[12px] font-semibold text-[#718096]">
                Documents
              </label>
              {expense.documentCompleteness && (
                <span className={`text-[11px] font-medium ${
                  expense.documentCompleteness.status === 'Complete' ? 'text-[#10B981]' :
                  expense.documentCompleteness.status === 'Incomplete' ? 'text-[#C9A227]' :
                  'text-[#A0AEC0]'
                }`}>
                  {expense.documentCompleteness.status === 'Complete' ? 'Documentation complete' :
                   expense.documentCompleteness.status === 'Incomplete' ? 'Documentation incomplete' :
                   'No documents'}
                </span>
              )}
            </div>
            <DocumentList
              expenseId={expense.id}
              documents={localDocs}
              onDocumentDeleted={handleDocumentDeleted}
            />
            <div className="mt-3 space-y-3">
              <DocumentUploader
                expenseId={expense.id}
                onUploadComplete={handleUploadComplete}
                requiresLMN={expense.documentCompleteness?.requiresLMN}
              />
              {/* Link from Library */}
              <button
                onClick={() => setShowLibraryPicker(!showLibraryPicker)}
                className="flex items-center gap-2 text-[13px] text-[#1B365D] hover:text-[#C9A227] transition-colors"
              >
                <Library size={14} />
                {showLibraryPicker ? 'Hide library' : 'Link from library'}
              </button>
              {showLibraryPicker && (
                <div className="border border-[#E2E8F0] rounded-lg p-3 max-h-[200px] overflow-auto">
                  {loadingLibrary ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={16} className="animate-spin text-[#718096]" />
                    </div>
                  ) : libraryDocs.length === 0 ? (
                    <div className="text-[12px] text-[#A0AEC0] text-center py-3">
                      No unlinked documents in your library.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {libraryDocs.map(doc => (
                        <button
                          key={doc.id}
                          onClick={() => handleLinkFromLibrary(doc)}
                          disabled={linkingDocId === doc.id}
                          className="w-full flex items-center gap-2 p-2 rounded hover:bg-[#F7FAFC] text-left transition-colors"
                        >
                          {linkingDocId === doc.id ? (
                            <Loader2 size={14} className="animate-spin text-[#1B365D]" />
                          ) : (
                            <Library size={14} className="text-[#718096]" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium text-[#1B365D] truncate">{doc.fileName}</div>
                            <div className="text-[10px] text-[#A0AEC0]">
                              {doc.documentType} &middot; {(doc.fileSize / 1024).toFixed(0)} KB
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Reimbursement History */}
          {(activeReimbursements.length > 0 || cancelledReimbursements.length > 0) && (
            <div className="border-t border-[#E2E8F0] pt-4">
              <label className="block text-[12px] font-semibold text-[#718096] mb-3">
                Reimbursement History
              </label>

              <div className="space-y-3">
                {activeReimbursements.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between bg-[#F7FAFC] rounded-lg p-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-[#1B365D] font-mono">
                          {formatCurrency(r.amount)}
                        </span>
                        {r.hsaAccountName && (
                          <span className="text-[12px] text-[#718096]">
                            from {r.hsaAccountName}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[#718096] mt-0.5">
                        {formatDate(r.reimbursementDate)}
                        {r.notes && <span> &middot; {r.notes}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-2">
                      {onEditReimbursement && (
                        <button
                          className="text-[12px] text-[#1B365D] hover:underline"
                          onClick={() => onEditReimbursement(r)}
                        >
                          Edit
                        </button>
                      )}
                      {onCancelReimbursement && (
                        <button
                          className="text-[12px] text-red-600 hover:underline"
                          onClick={() => {
                            if (confirm('Cancel this reimbursement? It will be excluded from totals.')) {
                              onCancelReimbursement(r.id);
                            }
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {cancelledReimbursements.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between bg-gray-50 rounded-lg p-3 opacity-60"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-mono line-through text-[#718096]">
                          {formatCurrency(r.amount)}
                        </span>
                        <Badge variant="gray" size="default">Cancelled</Badge>
                      </div>
                      <div className="text-[12px] text-[#718096] mt-0.5">
                        {formatDate(r.reimbursementDate)}
                        {r.hsaAccountName && <span> from {r.hsaAccountName}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
            <div>
              {onDelete && (
                <Button variant="destructive" size="small" onClick={() => onDelete(expense.id)}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="default" onClick={onClose}>
                Close
              </Button>
              {expense.status !== 'FullyReimbursed' && onReimburse && (
                <Button variant="primary" size="default" onClick={() => onReimburse(expense)}>
                  Record Reimbursement
                </Button>
              )}
            </div>
          </div>
        </div>
    </AccessibleDialog>
  );
}
